import React, {useCallback} from 'react';
import {observer} from 'mobx-react';
import I18n from 'app/i18n';
import MyBetInterface from 'app/interfaces/MyBetInterface';
import {betMoney} from 'app/utils';
import Get from 'app/utils/Get';
import Post from 'app/utils/Post';
import {modalConfirm} from 'app/utils/modals/popups';
import {toastError, toastSuccess} from 'app/components/toasts/liteToast';
import {buyoutStore} from 'app/store/BuyoutStore';
import {configStore} from 'app/store/global';
import SmartLoader from 'app/components/reusable/SmartLoader';

interface PropsInterface {
    bet: MyBetInterface;
}

const requestBuyout = (bet: MyBetInterface, quote: string): void => {
    new Post({params: {quote}, url: `/bets/${bet.id}/buyout`})
        .execute()
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                buyoutStore.setPending(bet.id, data.buyout);
                toastSuccess(I18n.t('buyout.requested'));
            } else {
                toastError(data.message || I18n.t('buyout.unavailable'));
            }
        });
};

const quoteBuyout = (bet: MyBetInterface): void => {
    if (buyoutStore.isQuoting(bet.id)) {
        return;
    }

    buyoutStore.setQuoting(bet.id, true);
    new Get({url: `/bets/${bet.id}/buyout_quote`})
        .execute()
        .then(response => response.json())
        .then(data => {
            if (!data.success) {
                toastError(data.message || I18n.t('buyout.unavailable'));
                return;
            }
            modalConfirm(
                I18n.t('buyout.confirm', {
                    amount: betMoney({amount: Number(data.amount), playmoney: bet.playmoney})
                }),
                () => requestBuyout(bet, data.quote)
            );
        })
        .finally(() => buyoutStore.setQuoting(bet.id, false));
};

const BuyoutButton = observer(({bet}: PropsInterface): React.ReactElement | null => {
    const onClick = useCallback((e: React.MouseEvent): void => {
        e.preventDefault();
        quoteBuyout(bet);
    }, [bet]);

    if (bet.bought_out) {
        return null;
    }

    if (buyoutStore.pendingFor(bet)) {
        return <button className="button-adaptive button-adaptive_instyle history-buyout-button" disabled>
            <SmartLoader loaderClassName="history-buyout-loader"/>
            <span className="button-name">{I18n.t('buyout.pending')}</span>
        </button>;
    }

    if (!bet.buyout_available) {
        return null;
    }

    const amount = buyoutStore.amountFor(bet, configStore.buyoutConfig);

    if (amount === null) {
        return null;
    }

    return <button className="button-adaptive button-adaptive_instyle history-buyout-button"
        disabled={buyoutStore.isQuoting(bet.id)}
        title={I18n.t('buyout.label')}
        onClick={onClick}
    >
        {buyoutStore.isQuoting(bet.id) && <SmartLoader loaderClassName="history-buyout-loader"/>}
        <span className="button-name">
            {I18n.t('buyout.button')} {betMoney({amount, playmoney: bet.playmoney})}
        </span>
    </button>;
});

export default BuyoutButton;
